import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import type { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { BulkSignJobStatus } from '@dealstack/shared/lib/common/eideasy/bulkSigning';
import type { BulkSignJobStatusRes } from '@dealstack/shared/lib/generated';
import {
	createPollingRefetchInterval,
	POLLING_MAX_CONSECUTIVE_ERRORS,
} from '@dealstack/frontend/shared/dataExchange/reactQuery/pollingRefetchInterval';
import type { BulkProgress } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { toErrorKey } from './useEidEasySign.utils';

const POLL_INTERVAL_MS = 2000;

/** Stop polling after 25 minutes (within the 45-min Temporal workflow execution timeout). */
const MAX_POLL_DURATION_MS = 25 * 60 * 1000;

/** Slower cadence while the poll is erroring (e.g. a transient 429), so backoff
 *  stays comfortably under the 120/min status-endpoint budget. */
const POLL_ERROR_BACKOFF_MS = 6000;

interface BulkSignJobStatusResponse {
	payload: BulkSignJobStatusRes;
}

type PollingPhase = 'prepare' | 'complete';

export interface PollingCallbacks {
	onPrepareReady?: (data: BulkSignJobStatusRes) => void;
	onPrepareComplete?: (data: BulkSignJobStatusRes) => void;
	onCompleteFinished?: () => void;
	onFailed?: (error: string) => void;
	onProgress?: (progress: BulkProgress) => void;
}

interface UseBulkSignPollingOptions {
	/**
	 * Base path for the default status endpoint builder. Required unless a custom
	 * `buildStatusEndpoint` is supplied (e.g. the public programme-token flow,
	 * whose route base and `?token=` query don't fit the admin shape).
	 */
	basePath?: string;
	callbacks: PollingCallbacks;
	/** When 'draw', use completedCount for prepare progress instead of preparedCount */
	isDrawMethod?: boolean;
	/**
	 * Override the polled status-endpoint URL. Defaults to the admin
	 * `${basePath}/eid-easy/bulk-sign/:jobId/status` route.
	 */
	buildStatusEndpoint?: (jobId: string) => string;
	/**
	 * Extra request options forwarded to `makeRequest` (e.g. `skipLoginRedirect`
	 * for the unauthenticated public flow). Defaults to the admin behavior (none).
	 */
	requestOptions?: ServiceRequestOptions;
	/** React Query cache-key prefix; defaults to the admin key. */
	queryKeyPrefix?: string;
	/** Sentry `signingFlow` tag; defaults to the admin `'bulk'`. */
	sentryFlowTag?: string;
}

export const useBulkSignPolling = ({
	basePath,
	callbacks,
	isDrawMethod = false,
	buildStatusEndpoint = (jobId) => `${basePath}/eid-easy/bulk-sign/${jobId}/status`,
	requestOptions,
	queryKeyPrefix = 'eid-easy-bulk-sign-job',
	sentryFlowTag = 'bulk',
}: UseBulkSignPollingOptions) => {
	const [activeJobId, setActiveJobId] = useState<string | null>(null);
	const [pollingPhase, setPollingPhase] = useState<PollingPhase | null>(null);
	const pollStartTimeRef = useRef<number | null>(null);
	const activeJobIdRef = useRef<string | null>(null);
	const consecutiveErrorsRef = useRef(0);

	const isPollingTimedOut = () =>
		pollStartTimeRef.current !== null &&
		Date.now() - pollStartTimeRef.current > MAX_POLL_DURATION_MS;

	const startPolling = useCallback((jobId: string, phase: PollingPhase) => {
		activeJobIdRef.current = jobId;
		setActiveJobId(jobId);
		setPollingPhase(phase);
		pollStartTimeRef.current = Date.now();
		consecutiveErrorsRef.current = 0;
	}, []);

	const stopPolling = useCallback(() => {
		pollStartTimeRef.current = null;
		activeJobIdRef.current = null;
		setActiveJobId(null);
		setPollingPhase(null);
	}, []);

	const startCompletePhase = useCallback(() => {
		pollStartTimeRef.current = Date.now();
		setPollingPhase('complete');
		consecutiveErrorsRef.current = 0;
	}, []);

	const q = useQuery({
		queryKey: [queryKeyPrefix, activeJobId, pollingPhase],
		queryFn: async () => {
			const result = await makeRequest<BulkSignJobStatusResponse>(
				{
					endpoint: buildStatusEndpoint(activeJobId ?? ''),
					method: HTTP_METHOD.GET,
				},
				requestOptions
			);
			return result.payload;
		},
		enabled: !!activeJobId && !!pollingPhase,
		refetchInterval: createPollingRefetchInterval<BulkSignJobStatusRes>({
			isTerminal: (data) =>
				isPollingTimedOut() ||
				(!!data &&
					(data.status === BulkSignJobStatus.READY_TO_SIGN ||
						data.status === BulkSignJobStatus.COMPLETED ||
						data.status === BulkSignJobStatus.FAILED)),
			intervalMs: POLL_INTERVAL_MS,
			errorBackoffMs: POLL_ERROR_BACKOFF_MS,
			maxConsecutiveErrors: POLLING_MAX_CONSECUTIVE_ERRORS,
		}),
		// Polling errors are handled below (stop + Sentry + onFailed); no toast.
		meta: { suppressErrorToast: true },
	});

	useEffect(() => {
		if (!q.isError) {
			return;
		}
		consecutiveErrorsRef.current += 1;
		if (consecutiveErrorsRef.current < POLLING_MAX_CONSECUTIVE_ERRORS) {
			// Transient failure (e.g. a 429 from the poll rate limit). Keep polling;
			// createPollingRefetchInterval backs off. Don't abort the signing flow.
			return;
		}
		const err = q.error;
		stopPolling();
		Sentry.captureException(err, {
			tags: { signingFlow: sentryFlowTag, phase: pollingPhase ?? 'unknown' },
			extra: { jobId: activeJobId },
		});
		callbacks.onFailed?.(toErrorKey(err));
	}, [q.isError, q.errorUpdateCount]);

	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		void (async () => {
			const data = q.data;
			consecutiveErrorsRef.current = 0;
			if (isPollingTimedOut()) {
				stopPolling();
				Sentry.captureMessage('Bulk sign polling timed out', {
					level: 'warning',
					tags: { signingFlow: sentryFlowTag, phase: pollingPhase ?? 'unknown' },
					extra: { jobId: activeJobId },
				});
				callbacks.onFailed?.('eid_easy_signing.error_job_timeout');
				return;
			}

			if (pollingPhase === 'prepare') {
				const progressCount = isDrawMethod ? data.completedCount : data.preparedCount;

				callbacks.onProgress?.({
					phase: 'preparing',
					current: progressCount,
					total: data.totalDocuments,
				});

				if (data.status === BulkSignJobStatus.COMPLETED) {
					stopPolling();
					callbacks.onPrepareComplete?.(data);
				} else if (data.status === BulkSignJobStatus.READY_TO_SIGN) {
					setPollingPhase(null);
					callbacks.onPrepareReady?.(data);
				} else if (data.status === BulkSignJobStatus.FAILED) {
					stopPolling();
					Sentry.captureMessage('Bulk sign prepare job failed', {
						level: 'error',
						tags: { signingFlow: sentryFlowTag, phase: 'prepare' },
						extra: { jobId: activeJobId, errors: data.errors },
					});
					callbacks.onFailed?.('eid_easy_signing.error_prepare_failed');
				}
			} else if (pollingPhase === 'complete') {
				callbacks.onProgress?.({
					phase: 'completing',
					current: data.completedCount,
					total: data.totalDocuments,
				});

				if (data.status === BulkSignJobStatus.COMPLETED) {
					stopPolling();
					callbacks.onCompleteFinished?.();
				} else if (data.status === BulkSignJobStatus.FAILED) {
					stopPolling();
					Sentry.captureMessage('Bulk sign complete job failed', {
						level: 'error',
						tags: { signingFlow: sentryFlowTag, phase: 'complete' },
						extra: { jobId: activeJobId, errors: data.errors },
					});
					callbacks.onFailed?.('eid_easy_signing.error_complete_failed');
				}
			}
		})();
	}, [q.isSuccess, q.data]);

	return {
		activeJobId,
		activeJobIdRef,
		pollingPhase,
		startPolling,
		stopPolling,
		startCompletePhase,
	};
};
